import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaterialModalBaseComponent } from './material-modal-base.component';

interface ModalClass<T extends MaterialModalBaseComponent> {
  new (...args): T;
  prototype: T;
}

interface OpenResult<T extends MaterialModalBaseComponent> {
  component: T;
  result$: T['result$'];
  modalRef: MatDialogRef<T, {}>;
}

@Injectable()
export class MaterialModalService {
  public modalRefs: any[] = [];

  constructor(private matDialogService: MatDialog) {}

  private errors$ = new Subject();
  private loading$ = new BehaviorSubject(false);

  openModal<T extends MaterialModalBaseComponent>(
    component: ModalClass<T>,
    data: { [key in keyof T]?: any } = null,
    size: { width?: any; height?: any; maxWidth?: any; maxHeight?: any } = {}
  ) {
    this.resetModalState();
    this.modalRefs.unshift(
      this.matDialogService.open(component, {
        data: {
          ...data,
          loading$: this.loading$.asObservable(),
          errors$: this.errors$.asObservable()
        },
        maxWidth: size.maxWidth || 'inherit',
        width: size.width || 'inherit'
      })
    );
    return this.getResult<T>();
  }

  closeModal() {
    if (this.modalRefs.length === 0) {
      return;
    }
    const modalRef = this.modalRefs.shift();
    modalRef.close();
  }

  closeAll() {
    this.modalRefs.forEach(modalRef => modalRef.close());
    this.modalRefs = [];
  }

  toggleModalLoading(value = !this.loading$.value) {
    this.loading$.next(value);
  }

  setModalErrors(errors) {
    this.toggleModalLoading(false);
    this.errors$.next(errors);
  }

  onModalError() {
    return catchError(
      (errors): Observable<boolean> => {
        this.setModalErrors(errors);
        return of(false);
      }
    );
  }

  private getResult<T extends MaterialModalBaseComponent>(): OpenResult<T> {
    if (this.modalRefs.length === 0) {
      return;
    }
    const modalRef = this.modalRefs[0];
    const modalComponent: T = modalRef.componentInstance;

    return {
      component: modalComponent,
      modalRef,
      result$: modalComponent.result$.pipe(
        tap(result => !result && this.closeModal())
      )
    };
  }

  private resetModalState() {
    this.errors$.next();
    this.loading$.next(false);
  }
}
